import { createSelector } from 'reselect';
import type { AccessControlReducer, AccessResource, AccessResourcePermissions } from '../types/accessControlTypes';
import '../types/accessControlTypes';
import { createInitialState } from '../reducers/AccessControlReducer';
import { hasAccess } from '../../common/support/utils/accessControlHelpers';
import { LOADING_STATUS as LoadingStatus } from '../support/constants';
import {
  AGENCY_TAGGED_DATA,
  AD_NETWORKS,
  REVENUE_DATA,
  READ_PERMISSION,
  APP_LEVEL_SETTINGS,
  CHANNEL_LEVEL_SETTINGS,
  WRITE_PERMISSION,
  LINK_LEVEL_SETTINGS,
} from '../../common/support/accessControlConstants';
import { ACCESS_FEATURE } from '../../../../shared/constants';
import { AccessRoleResource, PermissionLevel } from '../../../../shared/types';

/**
 * Return AccessControl reducer state from global redux state.
 */
export function getState(state: any): AccessControlReducer {
  return (state && state.accessControl) || createInitialState();
}

/**
 * Get loading status of Access Control
 */
export const getLoadingStatus: (state: any) => number = createSelector(getState, (state) => state.loadingStatus);
export const isAccessControlLoading: (state: any) => boolean = createSelector(getState, (state) => state.loadingStatus < 2);
export const getError: (state: any) => Record<string, any> | null | undefined = createSelector(
  getState,
  (state) => state.error
);

/**
 * Get all loaded roles for current user.
 */
export const getRoles: (state: any) => AccessResourcePermissions | undefined = createSelector(
  getState,
  (state) => state.data
);

export const selectAppDefaultRoles: (state: any) => AccessResourcePermissions | null | undefined = createSelector(
  getState,
  (state) => state.appDefaultRoles
);
export const selectAppDefaultRolesList: (state: any) => { id: string; name: AccessRoleResource }[] = createSelector(
  selectAppDefaultRoles,
  (appDefaultRoles) =>
    Object.entries(appDefaultRoles || {}).map(([id, name]) => ({
      id,
      name,
    }))
);
export const getAdNetworkPermission: (state: any) => number | null | undefined = createSelector(
  getRoles,
  (state) => state?.ad_networks.permission
);
export const isAuthorized: (
  state: any,
  access?: {
    permission: PermissionLevel;
    accessResources: AccessResource[];
  }
) => boolean | null | undefined = createSelector(
  getRoles,
  (_, access) => access,
  (roles, access) => {
    return access && hasAccess(access.accessResources, access.permission, roles);
  }
);
export const isLoading: (state: any) => Boolean = createSelector(getState, (state) => {
  if (!state.loadingStatus) {
    return true;
  }

  return Boolean(state.loadingStatus === LoadingStatus.LOADING);
});
export const selectHasAdNetworkOrAgencyTaggedDataRestrictions: (state: any) => boolean = createSelector(
  getRoles,
  (roles) => {
    return !hasAccess([AD_NETWORKS, AGENCY_TAGGED_DATA], READ_PERMISSION, roles);
  }
);
export const isRevenueDataRestricted: (state: any) => boolean = createSelector(getRoles, (roles) => {
  return !hasAccess([REVENUE_DATA], READ_PERMISSION, roles);
});
export const hasAppLevelSettingsWriteAccess: (state: any) => boolean = createSelector(getRoles, (roles) => {
  return hasAccess([APP_LEVEL_SETTINGS], WRITE_PERMISSION, roles);
});
export const hasChannelLevelSettingsWriteAccess: (state: any) => boolean = createSelector(getRoles, (roles) => {
  return hasAccess([CHANNEL_LEVEL_SETTINGS], WRITE_PERMISSION, roles);
});
export const getHasLinkLevelWriteAccess: (state: any) => boolean = createSelector(getRoles, (roles) => {
  return hasAccess([LINK_LEVEL_SETTINGS], WRITE_PERMISSION, roles);
});
export const getHasTemplateOnlyAccess: (state: any) => boolean = createSelector(getRoles, (roles) => {
  return (roles?.link_level_settings?.access_filter || []).includes(ACCESS_FEATURE.TEMPLATE_ONLY);
});
